import React, { useState, useEffect, useRef } from "react";
import {
  ServicesCheckBoxListItem,
  TextFieldWrapper,
  selectMenuProps,
  StyledMenuItem,
  StyledMenuItemCheckbox,
} from "../style";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
  Chip,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { POD_ONBOARDING } from "utility/constants";
import {
  customiseMaterialUITextBoxStyle,
} from "utility/styles";
import { FormError } from "shared-compoents/form-components";
import { useSelector } from "react-redux";
import { getRoles } from "../ducks/selectors";
import { AddUserProps } from "../ducks/types";
import { getFilteredRoles } from "utility/helper";

function AddUser({
  field,
  removeUserHandler,
  index,
  register,
  errors,
  setValue,
  isSubmitted,
}: AddUserProps) {
  const roles = useSelector(getRoles);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    field.roles || []
  );
  const [isRoleTouched, setIsRoleTouched] = useState(false);
  const isFirstRender = useRef(true);

  // Update form value when selectedRoles changes, skip validation on initial mount
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setValue(`users.${index}.roles`, selectedRoles);
      return;
    }
    setValue(`users.${index}.roles`, selectedRoles, { shouldValidate: true });
  }, [selectedRoles, index, setValue]);

  const handleRoleChange = (event: any) => {
    const value = event.target.value;
    const roleNames = typeof value === "string" ? value.split(",") : value;
    setSelectedRoles(roleNames);
    setIsRoleTouched(true);
  };

  return (
    <ServicesCheckBoxListItem key={index + 1}>
      {index > 0 && (
        <Tooltip title="Remove user" placement="top">
          <IconButton
            onClick={removeUserHandler(index)}
            size="small"
            sx={{
              position: "absolute",
              right: 4,
              top: -12,
              color: "error.main",
              "&:hover": {
                backgroundColor: "rgba(211,47,47,0.08)",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <TextFieldWrapper>
        <TextField
          label={POD_ONBOARDING.userEmailLabel}
          margin="normal"
          sx={customiseMaterialUITextBoxStyle}
          {...register(`users.${index}.email`)}
          id={`user-email-${index}`}
        />
        {errors && errors?.users?.[index] && errors?.users?.[index]?.email ? (
          <FormError>{errors?.users?.[index]?.email?.message}</FormError>
        ) : (
          <></>
        )}
      </TextFieldWrapper>
      <TextFieldWrapper>
        <FormControl
          fullWidth
          margin="normal"
          sx={customiseMaterialUITextBoxStyle}
        >
          <InputLabel id={`role-select-label-${index}`}>
            {POD_ONBOARDING.userRoleLabel}
          </InputLabel>
          <Select
            labelId={`role-select-label-${index}`}
            multiple
            value={selectedRoles}
            onChange={handleRoleChange}
            input={<OutlinedInput label={POD_ONBOARDING.userRoleLabel} />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    size="small"
                    onDelete={() => {
                      const newSelectedRoles = selectedRoles.filter(
                        (name) => name !== value
                      );
                      setSelectedRoles(newSelectedRoles);
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                  />
                ))}
              </Box>
            )}
            MenuProps={selectMenuProps}
          >
            {roles.length > 0 &&
              getFilteredRoles(roles).map((role) => (
                <StyledMenuItem key={role.sys_id} value={role.u_primary}>
                  <StyledMenuItemCheckbox
                    checked={selectedRoles.includes(role.u_primary)}
                  />
                  <ListItemText primary={role.u_primary} />
                </StyledMenuItem>
              ))}
          </Select>
        </FormControl>
        {(isRoleTouched || isSubmitted) && errors?.users?.[index]?.roles ? (
          <FormError>{errors?.users?.[index]?.roles?.message}</FormError>
        ) : null}
      </TextFieldWrapper>
    </ServicesCheckBoxListItem>
  );
}

export default AddUser;
